import { SessionControls } from "../authorization/session_controls";
import { ImportEditorRepository } from "../task_import/import_repository";
import { CoreButton } from "../../core/ui/button/button";
import { CoreInput } from "../../core/ui/input/input";
import { CoreSelect } from "../../core/ui/select/select";
import { CoreText, CoreTextType } from "../../core/ui/text/text";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useBlocker } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { TaskEditorStore, taskLabels } from "./task_editor_store";
import { TypesTasks } from "./task_model";
import { FieldError, TaskEditor } from "./task_editors";
import "./task.css";
import { MarkdownEditor } from "../../core/ui/markdown/markdown_editor";
import { MarkdownPreview } from "../../core/ui/markdown/markdown_preview";
import { TaskConfirmDialog } from "./task_confirm_dialog";

export const TaskEditorScreen = observer(() => {
  const { id: taskId, batch, entry } = useParams();
  const id = entry || taskId;
  const navigate = useNavigate();
  const [store] = useState(
    () =>
      new TaskEditorStore(
        batch && entry ? new ImportEditorRepository(batch, entry) : undefined,
      ),
  );
  const [nextType, setNextType] = useState<TypesTasks>();
  const blocker = useBlocker(store.dirty || store.saving || store.uploading);
  const save = async (status: "draft" | "published") => {
    const savedId = await store.save(status);
    if (savedId && !id) navigate(`/task/${savedId}/edit`, { replace: true });
  };
  useEffect(() => {
    store.load(id);
  }, [store, id]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (store.dirty || store.saving || store.uploading) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [store]);
  return (
    <main className="task-page">
      <SessionControls />
      <Link to={batch ? `/task/import/${batch}` : "/task"}>
        {batch ? "← Предпросмотр импорта" : "← Банк заданий"}
      </Link>
      {batch && (
        <p className="muted">
          Исправления сохраняются в пакет. Создание черновика выполняется на
          странице импорта.
        </p>
      )}
      <header className="task-header">
        <div>
          <CoreText
            as="h1"
            type={CoreTextType.big}
            text={store.model._id ? "Редактирование задания" : "Новое задание"}
          />
        </div>
        <span className={`status-badge ${store.model.status}`}>
          {store.model.status === "published" ? "Опубликовано" : "Черновик"}
        </span>
      </header>
      {store.loading && <p role="status">Загрузка задания…</p>}
      {store.error && (
        <div role="alert" className="error-banner">
          {store.error}
          {!store.ready && (
            <CoreButton onClick={() => store.load(id)}>Повторить</CoreButton>
          )}
        </div>
      )}
      {store.notice && (
        <p className="success-banner" role="status">
          {batch ? "Изменения сохранены в предпросмотр импорта" : store.notice}
        </p>
      )}
      {store.ready && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            save("draft");
          }}
        >
          <fieldset
            disabled={store.saving || store.uploading}
            className="editor-fields"
          >
            <section className="panel">
              <CoreText
                as="h2"
                type={CoreTextType.header}
                text="Основные сведения"
              />
              <CoreInput
                native
                label="Название"
                value={store.model.task.title}
                invalid={!!store.fieldErrors["task.title"]}
                onChange={(value) => store.setTitle(value)}
              />
              <FieldError store={store} path="task.title" />
              <div className="two-columns">
                <div>
                  <CoreSelect
                    label="Тип задания"
                    value={store.model.task.type}
                    onChange={(value) => {
                      const type = value as TypesTasks;
                      if (
                        store.answers.length &&
                        type !== store.model.task.type
                      )
                        setNextType(type);
                      else store.changeType(type);
                    }}
                    options={Object.entries(taskLabels).map(
                      ([value, label]) => ({ value, label }),
                    )}
                  />
                </div>
                <div>
                  <CoreInput
                    native
                    label="Баллы"
                    inputMode="decimal"
                    value={store.score}
                    invalid={!!store.fieldErrors.scoreAmount}
                    onChange={(value) => store.setScore(value)}
                  />
                  <FieldError store={store} path="scoreAmount" />
                </div>
              </div>
              <label className="random-eligibility">
                <input
                  type="checkbox"
                  checked={store.model.randomEligible !== false}
                  onChange={(event) =>
                    store.setRandomEligible(event.target.checked)
                  }
                />
                Доступно в случайных тестах
              </label>
              <p className="muted">
                Если отключено, задание может добавить в подборку только
                администратор.
              </p>
              <label>Теги</label>
              <div className="tags">
                {store.model.tags.map((tag) => (
                  <span className="tag" key={tag}>
                    {tag}
                    <CoreButton
                      type="button"
                      style={{ padding: "0 4px", border: 0 }}
                      aria-label={`Удалить тег ${tag}`}
                      onClick={() => store.removeTag(tag)}
                    >
                      ×
                    </CoreButton>
                  </span>
                ))}
              </div>
              <div className="tag-input">
                <CoreInput
                  native
                  label="Новый тег"
                  value={store.tagInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      store.addTag();
                    }
                  }}
                  onChange={(value) => store.setTagInput(value)}
                />
                <CoreButton type="button" onClick={() => store.addTag()}>
                  Добавить тег
                </CoreButton>
              </div>
              <FieldError store={store} path="tags" />
            </section>
            <section className="panel">
              <CoreText as="h2" type={CoreTextType.header} text="Условие" />
              <MarkdownEditor
                label="Текст условия"
                value={store.model.task.description || ""}
                onChange={(value) => store.setDescription(value)}
                uploadImage={(file) => store.uploadImage(file)}
                busy={store.uploading}
                invalid={!!store.fieldErrors["task.description"]}
              />
              <FieldError store={store} path="task.description" />
            </section>
            <section className="panel">
              <CoreText
                as="h2"
                type={CoreTextType.header}
                text="Разбор / решение"
              />
              <p className="muted">
                Разбор доступен администратору и не включён в предпросмотр для
                ученика.
              </p>
              <MarkdownEditor
                label="Текст разбора"
                value={store.model.task.solution || ""}
                onChange={(value) => store.setSolution(value)}
                uploadImage={(file) => store.uploadImage(file)}
                busy={store.uploading}
                invalid={!!store.fieldErrors["task.solution"]}
              />
              <FieldError store={store} path="task.solution" />
            </section>
            <section className="panel">
              <CoreText as="h2" type={CoreTextType.header} text="Ответы" />
              <TaskEditor store={store} />
            </section>
            <section className="panel preview">
              <CoreText
                as="h2"
                type={CoreTextType.header}
                text="Предпросмотр для ученика"
              />
              <CoreText
                as="h3"
                type={CoreTextType.header}
                text={store.model.task.title || "Название задания"}
              />
              <MarkdownPreview
                value={
                  store.model.task.description || "Условие пока не заполнено"
                }
                label="Условие для ученика"
              />
              {[TypesTasks.oneOfSeveral, TypesTasks.severalOSeveral].includes(
                store.model.task.type,
              ) ? (
                store.answers.map((answer, index) => (
                  <label className="preview-option" key={index}>
                    <input
                      disabled
                      type={
                        store.model.task.type === TypesTasks.oneOfSeveral
                          ? "radio"
                          : "checkbox"
                      }
                    />{" "}
                    {answer.text || `Вариант ${index + 1}`}
                  </label>
                ))
              ) : (
                <CoreInput
                  native
                  disabled
                  label="Ответ ученика"
                  placeholder="Ответ ученика"
                />
              )}
              <p className="muted">Баллы: {store.score || "0"}</p>
            </section>
          </fieldset>
          <footer className="editor-footer panel">
            <span role="status">
              {store.uploading
                ? "Загрузка изображения…"
                : store.saving
                  ? "Сохранение…"
                  : store.dirty
                    ? "Есть несохранённые изменения"
                    : store.model._id
                      ? "Все изменения сохранены"
                      : "Задание ещё не сохранено"}
            </span>
            <div className="row-actions">
              <CoreButton
                type="submit"
                disabled={store.saving || store.uploading}
              >
                {store.model.status === "published"
                  ? "Снять с публикации и сохранить"
                  : batch
                    ? "Сохранить в пакет"
                    : "Сохранить черновик"}
              </CoreButton>
              {!batch && (
                <CoreButton
                  type="button"
                  filled
                  disabled={store.saving || store.uploading}
                  onClick={() => save("published")}
                >
                  {store.model.status === "published"
                    ? "Сохранить публикацию"
                    : "Опубликовать"}
                </CoreButton>
              )}
            </div>
          </footer>
          <p className="muted">
            Черновик сохраняется с заполненным названием и корректными ответами.
            Публикация меняет статус в банке заданий.
          </p>
        </form>
      )}
      {nextType && (
        <TaskConfirmDialog
          titleId="type-title"
          onCancel={() => setNextType(undefined)}
        >
          <CoreText
            as="h2"
            id="type-title"
            type={CoreTextType.header}
            text="Сменить тип задания?"
          />
          <p>
            Все введённые ответы будут удалены. Название, условие, баллы и теги
            сохранятся.
          </p>
          <div className="row-actions">
            <CoreButton onClick={() => setNextType(undefined)}>
              Отмена
            </CoreButton>
            <CoreButton
              style={{
                color: "var(--ui-error)",
                borderColor: "var(--ui-error)",
              }}
              onClick={() => {
                store.changeType(nextType);
                setNextType(undefined);
              }}
            >
              Сменить тип и удалить ответы
            </CoreButton>
          </div>
        </TaskConfirmDialog>
      )}
      {blocker.state === "blocked" && (
        <TaskConfirmDialog
          titleId="leave-title"
          onCancel={() => blocker.reset?.()}
        >
          <CoreText
            as="h2"
            id="leave-title"
            type={CoreTextType.header}
            text="Выйти из редактора?"
          />
          <p>
            {store.uploading
              ? "Дождитесь загрузки изображения."
              : store.saving
                ? "Дождитесь окончания сохранения."
                : "Несохранённые изменения будут потеряны."}
          </p>
          <div className="row-actions">
            <CoreButton onClick={() => blocker.reset()}>Остаться</CoreButton>
            <CoreButton
              disabled={store.saving || store.uploading}
              style={{
                color: "var(--ui-error)",
                borderColor: "var(--ui-error)",
              }}
              onClick={() => blocker.proceed()}
            >
              Выйти без сохранения
            </CoreButton>
          </div>
        </TaskConfirmDialog>
      )}
    </main>
  );
});
